import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Button, Box, Container, Theme, useMediaQuery, Typography, Alert, Snackbar, IconButton, Tooltip, DialogContent, Dialog, DialogActions, Checkbox, TextField, FormControlLabel, Card, CardContent, CardHeader, Divider } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import { LoadingButton } from '@mui/lab';
import { Copy, Trash2Icon, Plus } from 'lucide-react';
import ConfirmModal from '@/components/Molecules/Modal/ConfirmModal.component';
import { ContentCopy } from '@mui/icons-material';
import { createCustomerAdminQuery } from '@/services/customer/customer.options';
import { AutoComplete } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import DateTimePickerElement from '@/components/Organisms/Date/DateTimePicker.component';
import TimePickerElement from '@/components/Organisms/Date/TimePicker.component';
import utc from 'dayjs/plugin/utc';
// import useCustomerMutation from '@/hooks/fetches/useCustomerMutations';
import useCustomerMutation from '@/services/customer/customer.mutations';
import { createAgentsByCustomerQueryOptions, createAttachmentTypesByCustomerQueryOptions, createAttachmentTypesCountQueryOptions } from '@/services/ticket/ticket.options';
import CheckboxElement from '@/components/Molecules/Checkbox/CheckboxElement.component';
import { createRolesQueryOptions } from '@/services/rolesPermissions/rolesPermission.options';
dayjs.extend(utc);

const customerSchema = z.object({
  entName: z.string().min(1, { message: 'Name is required' }),
  entAddress: z.string().optional(),
  phone: z.string().optional(),
  open: z.custom<dayjs.Dayjs>((val) => val instanceof dayjs).optional(),
  close: z.custom<dayjs.Dayjs>((val) => val instanceof dayjs).optional(),
  emailMatch: z.string().optional(),
  attachmentTypes: z.array(z.object({
    ATID: z.number().optional(),
    Name: z.string().min(1, { message: 'Name is required' }),
    Desc: z.string().optional(),
    Required: z.boolean().optional(),
    Multiple: z.boolean().optional(),
    CatchAll: z.boolean().optional(),
    TicketCount: z.number().optional(),
    isNew: z.boolean().optional(),
    isDirty: z.boolean().optional(),
  })).optional(),
  role: z.object({
    id: z.number().optional(),
    label: z.string().optional(),
  }).optional(),
});

const initialCustomer = {
  entName: '',
  entAddress: '',
  phone: '',
  open: '',
  close: '',
  emailMatch: '',
  attachmentTypes: [],
  role: { id: undefined, label: '' },
}

type Customer = z.infer<typeof customerSchema>;

// Add this type definition
type AttachmentType = {
  ATID?: number;
  Name: string;
  Desc?: string;
  Required?: boolean;
  Multiple?: boolean;
  CatchAll?: boolean;
  TicketCount?: number;
  isNew?: boolean; // To track new unsaved items
  isDirty?: boolean; // To track modified items
};

interface Role {
  roleid: number;
  rolename: string;
  roledesc: string;
  PermssionSet: number;
  ScopeTypeName: string;
  ScopeTypeId: number;
}

const CustomerDrawer = () => {
  const navigate = useNavigate();
  const params = useParams({ from: '/_auth/customers/$customerId' });
  const { variant, title } = useSearch({ from: '/_auth/customers/$customerId' })
  const [activeStep, setActiveStep] = useState(0);

  const methods = useForm<Customer>({
    mode: 'all',
    defaultValues: initialCustomer,
    shouldUnregister: false,
    resolver: zodResolver(customerSchema)
  });

  const { append, fields, remove, replace } = useFieldArray({
    control: methods.control,
    name: 'attachmentTypes',
  });

  const customerAdmin = useQuery(createCustomerAdminQuery())
  const attachmentTypes = useSuspenseQuery(createAttachmentTypesByCustomerQueryOptions(params.customerId))
  const attachmentTypesCount = useSuspenseQuery(createAttachmentTypesCountQueryOptions())
  const allRoles = useQuery(createRolesQueryOptions())
  const agentsByCustomer = useQuery(createAgentsByCustomerQueryOptions(params.customerId))

  const { createCustomerMutation, updateCustomerMutation, deleteAttachmentTypeMutation, createAttachmentTypeMutation, updateAttachmentTypeMutation } = useCustomerMutation();

  const currentCustomer = customerAdmin?.data?.find((customer: any) => customer?.Customer?.CustomerEntId === params.customerId)
  // console.log('currentCustomer : ', currentCustomer)

  // Query workflow rule data based on ID

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));


  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [openCustomerFieldsDialog, setOpenCustomerFieldsDialog] = useState(false);

  // Open drawer when component mounts
  useEffect(() => {
    setIsOpen(true)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate({ to: '/customers' })
    }, 300) // Match this with MUI's transition duration
  }

  const handleCloseDrawer = () => {
    handleClose()
    setActiveStep(0);
    // methods.reset(editCustomerValues2);
    navigate({ to: '/customers' });
  };

  const handleSubmit: SubmitHandler<any> = (formValues: any) => {
    const dirtyOrNewAttachmentTypes = methods?.getValues('attachmentTypes')?.filter(
      (type: any) => type.isNew || type.isDirty
    );

    dirtyOrNewAttachmentTypes?.forEach((type: any) => {
      if (type.isNew) {
        createAttachmentTypeMutation.mutate({
          actionType: 'createAttachmentType',
          payload: {
            catchAll: type.CatchAll,
            cid: params.customerId,
            description: type.Desc,
            multiple: type.Multiple,
            name: type.Name,
            required: type.Required,
          }
        });
      } else {
        updateAttachmentTypeMutation.mutate({
          actionType: 'updateAttachmentType',
          payload: {
            atid: type.ATID,
            cid: params.customerId,
            name: type.Name,
            description: type.Desc,
            required: type.Required,
            multiple: type.Multiple,
            catchAll: type.CatchAll
          }
        });
      }
    });

    if (!methods.formState.isDirty) return
    if (variant === 'edit') {
      formValues.customerEntId = currentCustomer?.Customer?.CustomerEntId
      updateCustomerMutation.mutate({
        actionType: 'updateCustomer',
        payload: formValues
      })
    } else {
      createCustomerMutation.mutate({
        actionType: 'createCustomer',
        payload: formValues
      })
    }
    return undefined
  }

  const handleComplete = () => {
    console.log('handleComplete')
    console.log(methods.getValues())
  }

  const handleDelete = () => {
    console.log('handleDelete')
    setIsDeleting(true);
    // executeTicketTypeAction({
    //   actionType: 'deleteTicketType',
    //   payload: {
    //     TypeID: parsedTicketType?.TypeID,
    //   },
    // });
  };

  useEffect(() => {
    if (variant === 'edit') {
      console.log('attachmentTypes : ', attachmentTypes.data)
      console.log('attachmentTypesCount : ', attachmentTypesCount.data)
      methods.setValue('entName', title)
      methods.setValue('entAddress', currentCustomer?.Customer?.EntAddress)
      methods.setValue('phone', currentCustomer?.Customer?.Phone)
      methods.setValue('open', currentCustomer?.Customer?.Open ?
        dayjs(currentCustomer.Customer.Open).utc() : undefined)
      methods.setValue('close', currentCustomer?.Customer?.Close ?
        dayjs(currentCustomer.Customer.Close).utc() : undefined)
      methods.setValue('emailMatch', currentCustomer?.Customer?.EmailMatch)
      methods.setValue('attachmentTypes', attachmentTypes.data)
      methods.setValue('role', { id: currentCustomer?.Role?.roleid, label: currentCustomer?.Role?.rolename })

    }
  }, [variant, title])

  const handleAttachmentTypeChange = (index: number) => {
    const type = methods.getValues(`attachmentTypes.${index}`);
    if (!type.isNew) {  // Only mark as dirty if it's an existing record
      methods.setValue(`attachmentTypes.${index}.isDirty`, true);
    }
  };

  return (
    <DrawerLayout
      open={isOpen}
      onClose={handleCloseDrawer}
      anchor="right"
      keepMounted
      width={'60%'}
      Header={
        <DrawerHeader
          close={handleCloseDrawer}
          title={variant === 'edit' ? 'Edit Customer' : 'New Customer'}
        />
      }
    >
      <FormProvider {...methods}>
        {/* @ts-ignore */}
        <Container maxWidth='xl' component='form' onSubmit={methods.handleSubmit(handleSubmit)} sx={{
          overflowY: 'auto',
          mt: 5,
          height: '98%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          mb: 4,
        }}>
          <Grid size={12} display="flex" flexDirection="column" flexWrap="wrap" >
            {variant === 'edit' && (
              <Alert severity='info' sx={{
                mb: 4
              }}>
                This customer is being used on: {currentCustomer?.TicketCount} tickets
              </Alert>
            )}
            <Typography variant="h6" sx={{ mb: 4 }}>
              Customer Details
            </Typography>
            <Grid size={12} container rowGap={4} >

              <Grid size={6} sx={{
                p: 2
              }}>
                <TextBox
                  name='entName'
                  fullWidth
                  label='Name'
                  required
                />

              </Grid>
              <Grid size={6} sx={{
                p: 2
              }}>
                <AutoComplete
                  id="role"
                  options={allRoles?.data?.map((role: Role) => ({
                    id: role?.roleid,
                    label: role?.rolename,
                  }))}
                  loading={false}
                  autocompleteProps={{
                    id: 'role',
                    loadingText: 'Loading Roles...',
                  }}
                  label="Role"
                />
              </Grid>
            </Grid>

            <Grid size={12} container >
              <Grid size={6} sx={{
                p: 2
              }}>
                <TextBox
                  name='entAddress'
                  fullWidth
                  label='Address'
                // sx={{ mb: 4 }}
                />
              </Grid>
              <Grid size={6} sx={{
                p: 2
              }}>
                <TextBox
                  name='phone'
                  fullWidth
                  label='Phone'
                  // sx={{ mb: 4 }}
                  required
                />
              </Grid>
            </Grid>
            <Grid size={12} container >
              <Grid size={6} sx={{
                p: 2
              }}>

                <TimePickerElement
                  sx={{ width: '100%' }}
                  name='open'
                  isDate={true}
                  label='Open Time'
                  control={methods.control}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />

              </Grid>
              <Grid size={6} sx={{
                p: 2
              }}>

                <TimePickerElement
                  sx={{ width: '100%' }}
                  name='close'
                  isDate={true}
                  label='Close Time'
                  control={methods.control}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid size={6} sx={{
                p: 2
              }}>
                <TextBox
                  name='emailMatch'
                  fullWidth
                  label='Email Match (Ex: *@customer.com, joe.dirt@customer.com[, ...])'
                // sx={{ mb: 4 }}
                />
              </Grid>
              <Grid size={12} container>
                <Grid size={12}>

                  <Box sx={{ p: 2 }} >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Attachment Types ({fields.length})
                    </Typography>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Plus size={20} />}
                        onClick={() => append({
                          Name: '',
                          Desc: '',
                          Required: false,
                          Multiple: false,
                          CatchAll: false,
                          isNew: true,
                          isDirty: false
                        })}
                      >
                        Add Type
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                {fields.map((type: AttachmentType, index: number) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }} key={index} sx={{ p: 2 }}>
                    <Card>
                      <CardHeader
                        title={fields[index].Name || 'New Attachment Type'}
                        subheader={`Number of attachments using this type: ${attachmentTypesCount.data?.find(
                          (t: any) => t.AttachmentTypeId === fields[index].ATID
                        )?.AttachmentCount || '---'
                          }`}
                      />
                      <CardContent>
                        <TextBox
                          name={`attachmentTypes.${index}.Name`}
                          fullWidth
                          label='Name'
                          required
                          sx={{ mb: 4 }}
                          onChange={() => handleAttachmentTypeChange(index)}
                        />
                        <TextBox
                          name={`attachmentTypes.${index}.Desc`}
                          fullWidth
                          label='Description'
                          multiline
                          rows={3}
                          sx={{ mb: 4 }}
                          onChange={() => handleAttachmentTypeChange(index)}
                        />
                        <CheckboxElement
                          id='isRequired'
                          label='Is Required'
                          name={`attachmentTypes.${index}.Required`}
                          onChange={() => handleAttachmentTypeChange(index)}
                        />
                        <CheckboxElement
                          id='allowMultiple'
                          label='Allow Multiple'
                          name={`attachmentTypes.${index}.Multiple`}
                          onChange={() => handleAttachmentTypeChange(index)}
                        />
                        <CheckboxElement
                          id='catchAll'
                          label='Catch All'
                          name={`attachmentTypes.${index}.CatchAll`}
                          onChange={() => handleAttachmentTypeChange(index)}
                        />
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                          {/* {(fields[index].isNew || fields[index].isDirty) && (
                            <LoadingButton
                              variant="contained"
                              size="small"
                              loading={createAttachmentTypeMutation.isPending || updateAttachmentTypeMutation.isPending}
                              onClick={() => {
                                const typeData = methods.getValues(`attachmentTypes.${index}`);
                                if (typeData.isNew) {
                                  createAttachmentTypeMutation.mutate({
                                    actionType: 'createAttachmentType',
                                    payload: {
                                      customerEntId: params.customerId,
                                      Name: typeData.Name,
                                      Desc: typeData.Desc,
                                      Required: typeData.Required,
                                      Multiple: typeData.Multiple,
                                      CatchAll: typeData.CatchAll
                                    }
                                  }, {
                                    onSuccess: (response) => {
                                      methods.setValue(`attachmentTypes.${index}.ATID`, response.ATID);
                                      methods.setValue(`attachmentTypes.${index}.isNew`, false);
                                      methods.setValue(`attachmentTypes.${index}.isDirty`, false);
                                    }
                                  });
                                } else {
                                  updateAttachmentTypeMutation.mutate({
                                    actionType: 'updateAttachmentType',
                                    payload: {
                                      ATID: typeData.ATID,
                                      customerEntId: params.customerId,
                                      Name: typeData.Name,
                                      Desc: typeData.Desc,
                                      Required: typeData.Required,
                                      Multiple: typeData.Multiple,
                                      CatchAll: typeData.CatchAll
                                    }
                                  }, {
                                    onSuccess: () => {
                                      methods.setValue(`attachmentTypes.${index}.isDirty`, false);
                                    }
                                  });
                                }
                              }}
                            >
                              Save
                            </LoadingButton>
                          )} */}
                          <LoadingButton
                            variant="outlined"
                            startIcon={<Trash2Icon size={20} />}
                            color="error"
                            size="small"
                            loading={deleteAttachmentTypeMutation.isPending}
                            onClick={() => {
                              if (fields[index].ATID) {
                                deleteAttachmentTypeMutation.mutate({
                                  actionType: 'deleteAttachmentType',
                                  payload: {
                                    atid: fields[index].ATID,
                                    // customerEntId: params.customerId
                                    // userName: agentsByCustomer?.data?.ContextUser.Name
                                  }
                                }, {
                                  onSuccess: () => {
                                    remove(index);
                                    attachmentTypes.refetch();
                                    attachmentTypesCount.refetch();
                                  }
                                });
                              } else {
                                remove(index);
                              }
                            }}
                          >
                            Delete
                          </LoadingButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              backgroundColor: (theme: Theme) => theme.palette.background.paper,
              borderTop: '1px solid',
              borderColor: (theme: Theme) => theme.palette.divider,
              p: 5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
            <LoadingButton
              loading={isDeleting}
              color="error"
              startIcon={<Trash2Icon />}
              size='small'
              onClick={() => setOpenModal(true)}
              disabled>
              Delete
            </LoadingButton>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleCloseDrawer} size='small'>
                Cancel & Close
              </Button>
              {openModal && (
                <ConfirmModal
                  open={openModal}
                  setOpen={setOpenModal}
                  title="Are you sure?"
                  content="This action will permanently delete this customer."
                  onSuccess={handleDelete}
                  onClose={() => setOpenModal(false)}
                  cancelButtonText="Cancel"
                  confirmButtonText="Delete"
                />
              )}
              <LoadingButton loading={isUpdating} variant="contained" type="submit" size='small'>
                Save
              </LoadingButton>
            </Box>
          </Box>
        </Container>

      </FormProvider>
    </DrawerLayout >
  )
}

export default CustomerDrawer